import {
  Form,
  Modal,
  Select,
  DatePicker,
  TimePicker,
  Input,
  Radio,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Divider,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import type { RadioChangeEvent } from "antd";
import { AppButton } from "components/button/AppButton";
import { useEffect } from "react";
import { IdentifierProps } from "types";
import {
  generalValidationRules,
  textInputValidationRulesOpt,
} from "utils/formHelpers/validations";
import { IJobInvite } from "../types";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { openNotification } from "utils/notification";
import { useSentJobInvite } from "../hooks/useSentJobInvite";
import { useGetJobs } from "features/jobs/hooks/useGetJobs";
import {
  PlusOutlined,
  DeleteOutlined,
  CalendarOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const MEETING_PLATFORMS = [
  { label: "Google Meet", value: "meet" },
  // { label: 'Zoom Meeting', value: 'zoom' },
  { label: "Microsoft Teams", value: "teams" },
];

export const SendInvite = ({ open, handleClose, id }: IdentifierProps) => {
  const [form] = Form.useForm();
  const { token, userInfo } = useGetUserInfo();
  const { mutate, isLoading } = useSentJobInvite();
  const { data, isLoading: jobDataLoading } = useGetJobs({});

  const handleFormSubmit = (values: any) => {
    const dates = values.interviewDate.map((item: any) =>
      item.time.format("YYYY-MM-DD HH:mm"),
    );

    const props: IJobInvite = {
      token,
      talent_id: id as unknown as string,
      listing_id: values.listing_id,
      interview_date: dates,
      meeting_platform: values.meeting_platform,
      note: values.note,
      userInfo,
    };

    mutate(props, {
      onError: (err: any) => {
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (err.response?.data?.error) {
          errorTitle = "Error: " + err.response.data.error;
          errorDescription =
            err.response.data.description || "Please try again.";
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
      },
      onSuccess: (res: any) => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Job invite sent successfully",
          duration: 4.5,
        });
        form.resetFields();
        handleClose();
      },
    });
  };

  useEffect(() => {
    const defaultField = { time: null };
    form.setFieldsValue({ interviewDate: [defaultField] });
  }, [form]);

  const handleAddDateField = () => {
    const fields = form.getFieldValue("interviewDate") || [];
    const newField = { time: null };
    form.setFieldsValue({ interviewDate: [...fields, newField] });
  };

  const handleRemoveDateField = (index: number) => {
    const fields = form.getFieldValue("interviewDate") || [];
    if (fields.length > 1) {
      form.setFieldsValue({
        interviewDate: fields.filter((_: any, i: number) => i !== index),
      });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    handleClose();
  };

  return (
    <Modal
      width="100%"
      style={{ maxWidth: 700, top: 20 }}
      footer={false}
      open={open}
      onCancel={handleCancel}
      title={
        <div className="text-center">
          <Title level={3} className="!mb-1">
            Schedule Interview
          </Title>
          <Text type="secondary">Send a job invite to the talent</Text>
        </div>
      }
      destroyOnClose
    >
      <Form
        onFinish={handleFormSubmit}
        form={form}
        layout="vertical"
        requiredMark="optional"
        className="mt-6"
      >
        {/* Job Selection */}
        <Card size="small" className="mb-4">
          <Form.Item
            name="listing_id"
            label={
              <Text strong>
                Select Job Position <span className="text-red-500">*</span>
              </Text>
            }
            rules={generalValidationRules}
          >
            <Select
              loading={jobDataLoading}
              placeholder="Choose a job listing..."
              allowClear
              showSearch
              optionFilterProp="label"
              labelInValue
              size="large"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={data?.data.map((item) => ({
                value: item.id,
                label: `${item.title} - ${item.id?.toString().slice(0, 8).toUpperCase()}`,
              }))}
            />
          </Form.Item>
        </Card>

        <Row gutter={[16, 16]}>
          {/* Meeting Platform */}
          <Col xs={24} md={12}>
            <Card size="small">
              <Form.Item
                name="meeting_platform"
                label={
                  <Space>
                    <VideoCameraOutlined />
                    <Text strong>
                      Meeting Platform <span className="text-red-500">*</span>
                    </Text>
                  </Space>
                }
                rules={generalValidationRules}
              >
                <Radio.Group className="w-full">
                  <Space direction="vertical" className="w-full">
                    {MEETING_PLATFORMS.map((platform) => (
                      <Radio
                        key={platform.value}
                        value={platform.value}
                        className="w-full p-2 border rounded-md hover:bg-gray-50"
                      >
                        {platform.label}
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </Form.Item>
            </Card>
          </Col>

          {/* Interview Dates */}
          <Col xs={24} md={12}>
            <Card size="small">
              <div className="flex justify-between items-center mb-4">
                <Space>
                  <CalendarOutlined />
                  <Text strong>
                    Interview Schedule <span className="text-red-500">*</span>
                  </Text>
                </Space>
                <Text type="secondary" className="text-xs">
                  {form.getFieldValue("interviewDate")?.length || 1} date(s)
                </Text>
              </div>

              <Form.List name="interviewDate">
                {(fields, { add, remove }, { errors }) => (
                  <div className="space-y-3">
                    {fields.map((field, index) => (
                      <div key={field.key} className="flex items-start gap-2">
                        <div className="flex-1">
                          <Form.Item
                            {...field}
                            name={[field.name, "time"]}
                            className="mb-0"
                            rules={[
                              {
                                required: true,
                                message: "Please select a time",
                              },
                            ]}
                          >
                            <TimePicker
                              use12Hours
                              format="hh:mm A"
                              minuteStep={1}
                              style={{ width: "100%" }}
                              placeholder="Select time"
                              showNow={false}
                              needConfirm={false}
                            />
                          </Form.Item>

                          <Form.ErrorList errors={errors} />
                        </div>

                        {fields.length > 1 && (
                          <DeleteOutlined
                            className="text-red-500 cursor-pointer mt-2"
                            onClick={() => remove(field.name)}
                          />
                        )}
                      </div>
                    ))}

                    <AppButton
                      label="Add Another Date"
                      handleClick={() => add({ time: null })}
                      variant="transparent"
                    />
                  </div>
                )}
              </Form.List>
            </Card>
          </Col>
        </Row>

        {/* Additional Notes */}
        <Card size="small" className="mt-4">
          <Form.Item
            name="note"
            label={<Text strong>Additional Notes (Optional)</Text>}
            rules={textInputValidationRulesOpt}
          >
            <TextArea
              rows={4}
              placeholder="Add any additional information, meeting links, or special instructions for the candidate..."
              showCount
              maxLength={500}
            />
          </Form.Item>
        </Card>

        <Divider />

        {/* Action Buttons */}
        <Form.Item className="mb-0">
          <div className="flex justify-end gap-3">
            <AppButton
              label="Cancel"
              handleClick={handleCancel}
              variant="transparent"
              disabled={isLoading}
            />
            <AppButton
              label="Send Invite"
              type="submit"
              isLoading={isLoading}
              disabled={jobDataLoading}
            />
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};